import {
  ALERT_SOURCE_FIELD_KEYS,
  type AlertSourceDetails,
} from '@kirin/api-client/contracts/alerts';

/** Query value so scan-result page can send "Back" to Alerts instead of Inventory. */
export const EXTENSION_SCAN_RESULT_FROM_ALERTS = 'alerts';

/** Query key carrying the alerts URL to restore (expanded row / occurrences). */
export const EXTENSION_SCAN_RESULT_RETURN_TO_PARAM = 'returnTo';

interface ExtensionScanResultLinkInput {
  location: string | null;
  sourceDetails?: AlertSourceDetails;
  organizationId?: string;
  /** When set (e.g. from Alerts), scan-result back nav returns here. */
  from?: typeof EXTENSION_SCAN_RESULT_FROM_ALERTS;
  /**
   * Current alerts URL (pathname + search) so Back restores the open alert
   * (expanded row and/or occurrences modal).
   */
  returnTo?: string;
}

function extensionIdFromLocation(location: string | null): string | null {
  if (!location) return null;
  const match = location
    .trim()
    .match(/^(?:[^:]+:)?([a-z0-9][a-z0-9_-]*\.[a-z0-9][a-z0-9_.-]*)/i);
  return match?.[1]?.toLowerCase() ?? null;
}

function extensionFieldValue(
  sourceDetails: AlertSourceDetails | undefined,
  key: string
): string | null {
  if (sourceDetails?.kind !== 'extension') return null;
  const field = sourceDetails.fields.find(item => item.key === key);
  if (!field || field.isUnknown) return null;
  const value = field.allowBlockValue?.trim() || field.detected?.trim();
  return value || null;
}

/**
 * Same-app relative path only — blocks open redirects (`//…`, `https://…`).
 */
export function isSafeScanResultReturnTo(
  value: string | null
): value is string {
  if (!value) return false;
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//')) return false;
  if (value.includes('://')) return false;
  return true;
}

const EXTENSION_SCAN_RESULT_PATH =
  /\/inventory\/extensions\/[^/]+\/scan-result\/?$/;

/**
 * True when the extension scan-result route was opened from Alerts
 * (`from=alerts` and/or a safe returnTo under `/alerts`).
 * Used so page chrome (header/sidebar tab) shows Alerts, not Inventory.
 */
export function isExtensionScanResultFromAlerts(
  pathname: string,
  searchParams: URLSearchParams
): boolean {
  if (!EXTENSION_SCAN_RESULT_PATH.test(pathname.replace(/\/+$/, ''))) {
    return false;
  }
  if (searchParams.get('from') === EXTENSION_SCAN_RESULT_FROM_ALERTS) {
    return true;
  }
  const returnTo = searchParams.get(EXTENSION_SCAN_RESULT_RETURN_TO_PARAM);
  return (
    isSafeScanResultReturnTo(returnTo) && /\/alerts(?:\/|$|\?)/.test(returnTo)
  );
}

/**
 * Builds the inventory scan-result route when an extension identity is
 * available from the alert payload (org or personal base).
 */
export function buildExtensionScanResultLink({
  location,
  sourceDetails,
  organizationId,
  from,
  returnTo,
}: ExtensionScanResultLinkInput): string | null {
  const extensionId =
    extensionFieldValue(sourceDetails, ALERT_SOURCE_FIELD_KEYS.NAME) ??
    extensionIdFromLocation(location);
  if (!extensionId) return null;

  const params = new URLSearchParams();
  const version = extensionFieldValue(
    sourceDetails,
    ALERT_SOURCE_FIELD_KEYS.VERSION
  );
  if (version) params.set('version', version);
  if (from) params.set('from', from);
  if (returnTo && isSafeScanResultReturnTo(returnTo)) {
    params.set(EXTENSION_SCAN_RESULT_RETURN_TO_PARAM, returnTo);
  }

  const base = organizationId
    ? `/${organizationId}/inventory/extensions/${encodeURIComponent(extensionId)}/scan-result`
    : `/dashboard/inventory/extensions/${encodeURIComponent(extensionId)}/scan-result`;
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}
